import { useMemo, useState, useCallback } from "react";
import { GroupedBarChart } from "../components/charts/GroupedBarChart";
import { DonutChart } from "../components/charts/DonutChart";
import {
  Box,
  Skeleton,
  Text,
  Progress,
  ScrollArea,
  useElementSize,
  useIsMobile,
} from "@salestrak/ui";
import type { TrendDirection } from "#app/lib/data";
import { GhostState } from "../components/GhostState";
import { useDashboardFilters } from "../lib/FilterContext";
import { CHART_PALETTE_WITH_GRADIENT } from "../lib/constants";
import { formatNumber } from "../lib/utils";
import { KpiCard } from "../components/KpiCard";
import { Section } from "../components/Section";
import { DrilldownDialog } from "../components/DrilldownDialog";
import type { DrilldownType } from "../components/DrilldownDialog";
import { AgeReasonMetrics } from "../components/AgeReasonMetrics";
import { GoldenHourChart } from "../components/GoldenHourChart";
import { KnockLeaderboard } from "../components/KnockLeaderboard";
import { LatestKnockActivity } from "../components/LatestKnockActivity";
import { InfoTooltip } from "../components/InfoTooltip";
import { at } from "#app/lib/utils/at";

const PALETTE_DOT_COLORS = [
  "purple",
  "teal",
  "blue",
  "yellow",
  "red",
  "green",
  "orange",
  "light-purple",
  "grey",
  "violet",
] as const;

// Ghost bar chart — alternating purple/teal pairs at varying heights
const GHOST_BAR_HEIGHTS: [number, number][] = [
  [55, 38], [80, 62], [40, 28], [95, 75], [48, 35], [70, 52], [85, 65],
];

// Chart colors as live var() strings. `fill`, `stroke` and `stop-color` are CSS
// properties, so these re-resolve on a theme flip with no JS involved.
const VIOLET = "var(--violet)";
const LIGHT_PURPLE = "var(--light-purple)";
const TEAL = "var(--teal)";
const CYAN = "var(--cyan)";
const MUTED_FG = "var(--muted-foreground)";
const BORDER = "var(--border)";
const BACKGROUND = "var(--background)";

export default function OverviewScreen() {
  const { startDate, endDate, analytics } = useDashboardFilters();

  const isMobile = useIsMobile();
  const [drilldown, setDrilldown] = useState<DrilldownType | null>(null);
  const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);

  // Box reports no layout of its own; `useElementSize` measures it with a
  // ResizeObserver.
  const { ref: chartRef, size: chartSize } = useElementSize<HTMLDivElement>();
  const chartWidth = chartSize.width;

  const yAxisTextStyle = useMemo(() => ({ color: MUTED_FG, fontSize: 10 }), []);

  // Pre-computed from backend
  const insp = analytics?.inspections;
  const inspSubtitle = insp
    ? `${insp.avgPerDay.toFixed(1)}/day (${insp.changePercent >= 0 ? "+" : ""}${insp.changePercent}%)`
    : undefined;
  const inspTrend = insp?.trend as TrendDirection | undefined;

  // Workflow distribution — map backend data to chart format
  const workflowDistribution = useMemo(() => {
    if (!analytics?.workflowDistribution) return null;
    return analytics.workflowDistribution.map((w, i) => {
      // The palette cycles, so every slice resolves however many workflows
      // the workspace has.
      const paint = at(
        CHART_PALETTE_WITH_GRADIENT,
        i % CHART_PALETTE_WITH_GRADIENT.length,
      );
      return {
        value: w.count,
        workflow: w.workflowName,
        workflowId: w.workflowId,
        color: paint.color,
        gradient: paint.gradient,
      };
    });
  }, [analytics?.workflowDistribution]);

  const totalIssues =
    workflowDistribution?.reduce((sum, w) => sum + w.value, 0) ?? 0;

  // Daily activity chart — derive bucket count from backend response
  const chartDays = Math.min(analytics?.dailyActivity?.length ?? 0, 90);
  const useWeekly = (isMobile && chartDays > 14) || chartDays >= 60;
  const dailyCounts = useMemo(() => {
    if (!analytics?.dailyActivity) return null;

    const buckets = analytics.dailyActivity;
    const ageCounts: Record<string, number> = {};
    const issueCounts: Record<string, number> = {};
    for (const b of buckets) {
      ageCounts[b.date] = b.ageUpdates;
      issueCounts[b.date] = b.newIssues;
    }

    const dayKeys = buckets.map((b) => b.date);
    const labelInterval = chartDays <= 14 ? 1 : chartDays <= 30 ? 3 : 5;

    if (!useWeekly) {
      return {
        kind: "daily" as const,
        dayKeys,
        ageCounts,
        issueCounts,
        labelInterval,
      };
    }

    const weeks: {
      startDate: string;
      endDate: string;
      age: number;
      issues: number;
    }[] = [];
    for (let i = 0; i < dayKeys.length; i += 7) {
      const chunk = dayKeys.slice(i, i + 7);
      let age = 0;
      let issues = 0;
      for (const d of chunk) {
        age += ageCounts[d] ?? 0;
        issues += issueCounts[d] ?? 0;
      }
      weeks.push({
        startDate: at(chunk, 0),
        endDate: at(chunk, -1),
        age,
        issues,
      });
    }
    return { kind: "weekly" as const, weeks };
  }, [analytics?.dailyActivity, chartDays, useWeekly]);

  const hasActivityData = useMemo(() => {
    if (!dailyCounts) return false;
    if (dailyCounts.kind === "daily") {
      return (
        Object.values(dailyCounts.ageCounts).some((v) => v > 0) ||
        Object.values(dailyCounts.issueCounts).some((v) => v > 0)
      );
    }
    return dailyCounts.weeks.some((w) => w.age > 0 || w.issues > 0);
  }, [dailyCounts]);

  const ghostOverlay = <Text size="sm" weight="medium">No data for this period</Text>;

  const ghostBars = useMemo(() =>
    GHOST_BAR_HEIGHTS.flatMap(([a, b], i) => [
      { key: `a-${i}`, height: a },
      { key: `b-${i}`, height: b },
    ]), []);

  const openDrilldown = useCallback(
    (barData: { label?: string; date: string; endDate?: string }) => {
      const label =
        barData.label ||
        new Date(barData.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        });
      setDrilldown({
        kind: "dailyActivity",
        date: barData.date,
        endDate: barData.endDate,
        label,
      });
    },
    [],
  );

  return (
    <>
      <Box direction="column" fill="flex">
        <ScrollArea>
          <Box paddingX="md" paddingTop="md" paddingBottom="xl">
            <Box direction="column" gap="md">
              {/* KPIs */}
              <Box gap="md" className={isMobile ? "grid grid-cols-1" : "grid grid-cols-2"}>
                <KpiCard
                  title="Age Updates"
                  value={analytics?.ageUpdates}
                  tooltip="Number of age-related activity updates recorded"
                  loading={!analytics}
                />
                <KpiCard
                  title={analytics?.labels?.targetMetricLabel ?? "Target"}
                  value={insp?.count ?? 0}
                  subtitle={inspSubtitle}
                  trend={inspTrend}
                  clickable={!!insp}
                  onClick={insp ? () => setDrilldown({ kind: "targetMetric" }) : undefined}
                  loading={!analytics}
                />
              </Box>

              {/* Age Reason Metrics */}
              <AgeReasonMetrics
                data={analytics?.ageReasons}
                labels={analytics?.labels}
                onClick={() => setDrilldown({ kind: "ageReasons" })}
              />

              {/* Golden Hour — hidden when no contact reasons configured */}
              {analytics?.goldenHour && (
                <GoldenHourChart
                  analyticsHourly={analytics.goldenHour.hourly}
                  analyticsAllTimeHourly={analytics.goldenHour.allTimeHourly}
                  analyticsGoldenRange={analytics.goldenHour.goldenRange}
                  labels={analytics.labels}
                  onHourPress={(hour, label) =>
                    setDrilldown({ kind: "goldenHour", hour, label })
                  }
                />
              )}

              {/* Top Knocks + Latest Knock */}
              <Box gap="md" className={isMobile ? "grid grid-cols-1" : "grid grid-cols-2"}>
                <KnockLeaderboard
                  entries={analytics?.knockLeaderboard}
                  labels={analytics?.labels}
                />
                <LatestKnockActivity
                  entries={analytics?.latestKnocks}
                  labels={analytics?.labels}
                />
              </Box>

              {/* Daily/Weekly Activity */}
              <Section
                title={`${useWeekly ? "Weekly" : "Daily"} Activity (${chartDays} days)`}
                titleExtra={
                  <InfoTooltip>
                    Age updates and new projects over time. Click a bar to see
                    that day's breakdown by member.
                  </InfoTooltip>
                }
              >
                <Box direction="row" gap="md">
                  <Box direction="row" gap="xs" align="center">
                    <Text size="xs" color="purple">
                      {"\u25CF"}
                    </Text>
                    <Text size="xs" variant="muted">
                      Age Updates
                    </Text>
                  </Box>
                  <Box direction="row" gap="xs" align="center">
                    <Text size="xs" color="teal">
                      {"\u25CF"}
                    </Text>
                    <Text size="xs" variant="muted">
                      New Projects
                    </Text>
                  </Box>
                </Box>
                <Box ref={chartRef} paddingTop="sm" overflow="hidden">
                  {!dailyCounts ? (
                    <Skeleton className="h-[220px] w-full" />
                  ) : !hasActivityData ? (
                    <Box paddingTop="xs">
                      <GhostState overlay={ghostOverlay}>
                        <Box direction="row" align="end" height={160} paddingX="xs" gap="xs">
                          {ghostBars.map((bar) => (
                            <Box key={bar.key} flex={1} height={bar.height} rounded="sm" background="muted" />
                          ))}
                        </Box>
                      </GhostState>
                    </Box>
                  ) : (
                    chartWidth > 0 &&
                    (() => {
                      const yAxisWidth = 40;
                      const initialSpacing = 10;
                      const available =
                        chartWidth - yAxisWidth - initialSpacing;
                      const groupCount =
                        dailyCounts.kind === "daily"
                          ? dailyCounts.dayKeys.length
                          : dailyCounts.weeks.length;
                      const pairGap = 2;
                      const groupGap = Math.max(
                        4,
                        Math.floor((available / groupCount) * 0.15),
                      );
                      const barWidth = Math.max(
                        4,
                        Math.floor(
                          (available -
                            groupGap * groupCount -
                            pairGap * groupCount) /
                            (groupCount * 2),
                        ),
                      );

                      const pairWidth = barWidth * 2 + pairGap;
                      const bars: any[] = [];
                      const labelTextStyle = {
                        color: MUTED_FG,
                        fontSize: 9,
                        textAlign: "center" as const,
                        width: pairWidth,
                      };
                      if (dailyCounts.kind === "daily") {
                        const {
                          dayKeys,
                          ageCounts,
                          issueCounts,
                          labelInterval,
                        } = dailyCounts;
                        for (let i = 0; i < dayKeys.length; i++) {
                          const date = at(dayKeys, i);
                          const isLast = i === dayKeys.length - 1;
                          const showLabel = i % labelInterval === 0;
                          const label = showLabel
                            ? new Date(date).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                timeZone: "UTC",
                              })
                            : "";
                          bars.push({
                            value: ageCounts[date] ?? 0,
                            frontColor: VIOLET,
                            gradientColor: LIGHT_PURPLE,
                            showGradient: true,
                            label,
                            date,
                            spacing: pairGap,
                            labelWidth: pairWidth,
                            labelTextStyle,
                          });
                          bars.push({
                            value: issueCounts[date] ?? 0,
                            frontColor: TEAL,
                            gradientColor: CYAN,
                            showGradient: true,
                            date,
                            ...(isLast && { spacing: 0 }),
                          });
                        }
                      } else {
                        const { weeks } = dailyCounts;
                        for (let i = 0; i < weeks.length; i++) {
                          const week = at(weeks, i);
                          const isLast = i === weeks.length - 1;
                          const label = new Date(
                            week.startDate,
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            timeZone: "UTC",
                          });
                          bars.push({
                            value: week.age,
                            frontColor: VIOLET,
                            gradientColor: LIGHT_PURPLE,
                            showGradient: true,
                            label,
                            date: week.startDate,
                            endDate: week.endDate,
                            spacing: pairGap,
                            labelWidth: pairWidth,
                            labelTextStyle,
                          });
                          bars.push({
                            value: week.issues,
                            frontColor: TEAL,
                            gradientColor: CYAN,
                            showGradient: true,
                            date: week.startDate,
                            endDate: week.endDate,
                            ...(isLast && { spacing: 0 }),
                          });
                        }
                      }

                      const totalAge = analytics?.ageUpdates ?? 0;
                      const chartHeight = 220;
                      const pitch = 2 * barWidth + pairGap + groupGap;
                      const actualGroupCount = bars.length / 2;

                      const hoverZones = Array.from(
                        { length: actualGroupCount },
                        (_, i) => {
                          const x = yAxisWidth + initialSpacing + i * pitch;
                          const barData = bars[i * 2];
                          const projectBarData = bars[i * 2 + 1];
                          const isHovered = hoveredGroup === i;
                          return (
                            <Box
                              key={i}
                              position="absolute"
                              left={x}
                              top={0}
                              width={pitch}
                              height={chartHeight}
                              cursor="pointer"
                              zIndex={2}
                              rounded="sm"
                              hoverBackground
                              onPointerEnter={() => setHoveredGroup(i)}
                              onPointerLeave={() =>
                                setHoveredGroup((current) =>
                                  current === i ? null : current,
                                )
                              }
                              onPointerUp={() => openDrilldown(barData)}
                            >
                              {isHovered && (
                                <Box
                                  pointerEvents="none" position="absolute"
                                  left={pitch / 2}
                                  top={6}
                                  paddingX="sm"
                                  paddingY="xs"
                                  rounded="sm"
                                  surface="popover"
                                  border
                                  borderColor="border"
                                  className="-translate-x-1/2"
                                  shrink={false}
                                  zIndex={3}
                                >
                                  <Text size="2xs" weight="medium" truncate={1}>
                                    Age Updates: {barData.value}
                                  </Text>
                                  <Text size="2xs" weight="medium" truncate={1}>
                                    New Projects: {projectBarData?.value ?? 0}
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          );
                        },
                      );

                      const barChart = (
                        <GroupedBarChart
                          key={`${totalAge}-${totalIssues}-${chartDays}`}
                          data={bars}
                          barWidth={barWidth}
                          noOfSections={4}
                          yAxisTextStyle={yAxisTextStyle}
                          height={chartHeight}
                          barBorderTopLeftRadius={4}
                          barBorderTopRightRadius={4}
                          rulesColor={BORDER}
                          dashWidth={4}
                          dashGap={4}
                          spacing={groupGap}
                          initialSpacing={initialSpacing}
                          width={available}
                          yAxisLabelWidth={yAxisWidth}
                          endSpacing={0}
                          onClick={(item) => openDrilldown(item as unknown as { label?: string; date: string; endDate?: string })}
                        />
                      );

                      return (
                        <Box position="relative">
                          {hoverZones}
                          {barChart}
                        </Box>
                      );
                    })()
                  )}
                </Box>
              </Section>

              {/* Workflow Issue Distribution */}
              <Section
                title="Workflow Project Distribution"
                titleExtra={
                  <InfoTooltip>
                    Distribution of all projects across workflows. Click a
                    workflow to see its projects broken down by member.
                  </InfoTooltip>
                }
              >
                {!workflowDistribution ? (
                  isMobile ? (
                    <Box direction="column" gap="md" align="center">
                      <Skeleton className="size-[150px] rounded-full" />
                      <Box direction="column" fill="flex" gap="xs">
                        {Array.from({ length: 3 }, (_, i) => (
                          <Skeleton key={i} className="h-3 w-full" />
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box direction="row" gap="sm">
                      <Box align="center" justify="center">
                        <Skeleton className="size-[150px] rounded-full" />
                      </Box>
                      <Box direction="column" fill="flex" gap="xs" justify="center">
                        {Array.from({ length: 3 }, (_, i) => (
                          <Skeleton key={i} className="h-3 w-full" />
                        ))}
                      </Box>
                    </Box>
                  )
                ) : workflowDistribution.length === 0 ? (
                  <Box align="center" justify="center" paddingY="lg">
                    <GhostState overlay={<Text size="sm" weight="medium">No data</Text>}>
                      <Box borderWidth={8} opacity={20} width={150} height={150} rounded="full" border borderColor="border" />
                    </GhostState>
                  </Box>
                ) : (
                  (() => {
                    const pieRadius = isMobile ? 65 : 75;
                    const pieInnerRadius = isMobile ? 38 : 45;
                    const pie = (
                      <DonutChart
                        data={workflowDistribution.map((item) => ({
                          value: item.value,
                          color: item.color,
                          gradientCenterColor: item.gradient,
                          showGradient: true,
                        }))}
                        radius={pieRadius}
                        innerRadius={pieInnerRadius}
                        innerCircleColor={BACKGROUND}
                        centerLabelComponent={() => (
                          <Box align="center" justify="center">
                            <Text size="lg" weight="bold">
                              {formatNumber(totalIssues)}
                            </Text>
                            <Text size="2xs" variant="muted">
                              Projects
                            </Text>
                          </Box>
                        )}
                        focusOnPress
                        sectionAutoFocus
                        initialAngle={-Math.PI / 2}
                      />
                    );
                    const list = (
                      <Box direction="column"
                        gap="xs"
                        justify={isMobile ? undefined : "center"}
                      >
                        {workflowDistribution.map((item, i) => {
                          const pct =
                            totalIssues > 0
                              ? (item.value / totalIssues) * 100
                              : 0;
                          return (
                            <Box
                              key={item.workflow}
                              onClick={() =>
                                setDrilldown({
                                  kind: "workflowIssues",
                                  workflow: item.workflow,
                                })
                              }
                            >
                              <Box direction="column" gap="xs" fill="width">
                                <Box direction="row" justify="between" align="center">
                                  <Box direction="row" gap="xs" align="center">
                                    <Text
                                      size="xs"
                                      color={
                                        PALETTE_DOT_COLORS[
                                          i % PALETTE_DOT_COLORS.length
                                        ]
                                      }
                                    >
                                      {"\u25CF"}
                                    </Text>
                                    <Text size="xs" truncate={1}>
                                      {item.workflow}
                                    </Text>
                                  </Box>
                                  <Text size="xs" weight="semibold">
                                    {item.value}{" "}
                                    <Text
                                      size="xs"
                                      variant="muted"
                                      weight="normal"
                                    >
                                      ({Math.round(pct)}%)
                                    </Text>
                                  </Text>
                                </Box>
                                <Progress value={pct} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                    return isMobile ? (
                      <Box direction="column" gap="md" align="center">
                        {pie}
                        {list}
                      </Box>
                    ) : (
                      <Box direction="row" gap="sm">
                        <Box align="center" justify="center">{pie}</Box>
                        {list}
                      </Box>
                    );
                  })()
                )}
              </Section>
            </Box>
          </Box>
        </ScrollArea>
      </Box>

      <DrilldownDialog
        drilldown={drilldown}
        onClose={() => setDrilldown(null)}
        startDate={startDate}
        endDate={endDate}
      />
    </>
  );
}
